import { Component } from "../lib/component.ts";

interface TileGalleryRefs {
  featured?: HTMLImageElement;

  video?: HTMLVideoElement;

  images?: HTMLDivElement;

  thumbs?: HTMLDivElement;

  thumbnails?: HTMLButtonElement[];
}

class TileGallery extends Component<TileGalleryRefs> {
  #hasPreloaded = false;

  #mediaCache: Map<string, HTMLImageElement | HTMLVideoElement> = new Map();

  #activeImageId: string | null = null;

  #featuredImageId: string | null = null;

  #pendingImageId: string | null = null;

  #hoverActivated = false;

  #hoverDelayTimer: number | null = null;

  #showThumbsTimer: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    const featuredId = this.refs.featured?.dataset.mediaId;
    if (featuredId && this.refs.featured) {
      this.#mediaCache.set(featuredId, this.refs.featured);
      this.#activeImageId = featuredId;
      this.#featuredImageId = featuredId;
    }

    const videoId = this.refs.video?.dataset.mediaId;
    if (videoId && this.refs.video) {
      this.#mediaCache.set(videoId, this.refs.video);
    }

    this.addEventListener("mouseenter", this.#handleGalleryEnter, { signal: this.signal });
    this.addEventListener("mouseleave", this.#handleGalleryLeave, { signal: this.signal });

    this.refs.thumbs?.addEventListener("mouseover", this.#handleThumbnailEnter, {
      signal: this.signal,
    });
    this.refs.thumbs?.addEventListener("focusin", this.#handleThumbnailEnter, {
      signal: this.signal,
    });
    this.refs.thumbs?.addEventListener("mouseleave", this.#handleThumbsLeave, {
      signal: this.signal,
    });
    this.refs.thumbs?.addEventListener("focusout", this.#handleThumbsFocusOut, {
      signal: this.signal,
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.#showThumbsTimer) clearTimeout(this.#showThumbsTimer);
    if (this.#hoverDelayTimer) clearTimeout(this.#hoverDelayTimer);
  }

  #handleGalleryEnter = (): void => {
    if (!this.#hasPreloaded) {
      this.#hasPreloaded = true;
      this.#preloadImages();
    }

    this.#showThumbsTimer = window.setTimeout(() => {
      this.refs.thumbs?.classList.add("is-visible");
    }, 100);
  };

  #handleGalleryLeave = (): void => {
    if (this.#showThumbsTimer) {
      clearTimeout(this.#showThumbsTimer);
      this.#showThumbsTimer = null;
    }
    this.refs.thumbs?.classList.remove("is-visible");
  };

  #preloadImages(): void {
    const sizes = this.dataset.sizes;
    const width = this.refs.featured?.width ?? 0;
    const height = this.refs.featured?.height ?? 0;
    const aspectRatio = this.refs.featured?.style.aspectRatio ?? "";

    for (const thumbnail of this.refs.thumbnails ?? []) {
      const mediaId = thumbnail.dataset.mediaId;
      if (!mediaId || this.#mediaCache.has(mediaId)) continue;

      const img = document.createElement("img");
      img.className = "product-tile__gallery-image";
      img.src = thumbnail.dataset.src ?? "";
      img.srcset = thumbnail.dataset.srcset ?? "";
      if (sizes) img.sizes = sizes;
      img.alt = thumbnail.dataset.alt ?? "";
      img.width = width;
      img.height = height;
      img.style.aspectRatio = aspectRatio;
      img.dataset.mediaId = mediaId;

      this.#mediaCache.set(mediaId, img);
      this.refs.images?.appendChild(img);
    }
  }

  #handleThumbnailEnter = (event: Event): void => {
    if (!(event.target instanceof HTMLElement)) return;
    const thumbnail = event.target.closest("[data-media-id]");
    if (!(thumbnail instanceof HTMLElement)) return;

    const mediaId = thumbnail.dataset.mediaId;
    if (!mediaId || mediaId === this.#activeImageId) return;

    this.#pendingImageId = mediaId;

    if (this.#hoverActivated) {
      this.#showThumbnailMedia(mediaId);
    } else {
      if (this.#hoverDelayTimer) clearTimeout(this.#hoverDelayTimer);
      this.#hoverDelayTimer = window.setTimeout(() => {
        this.#hoverActivated = true;
        if (this.#pendingImageId) {
          this.#showThumbnailMedia(this.#pendingImageId);
        }
      }, 50);
    }
  };

  async #showThumbnailMedia(mediaId: string): Promise<void> {
    const media = this.#mediaCache.get(mediaId);
    if (!media) return;

    if (media instanceof HTMLImageElement) {
      try {
        await media.decode();
      } catch {
        // Proceed to show image even if decode fails
      }
    }

    if (this.#pendingImageId === mediaId) {
      this.#showMedia(mediaId);
    }
  }

  #handleThumbsLeave = (): void => {
    this.#pendingImageId = null;
    this.#hoverActivated = false;
    if (this.#hoverDelayTimer) {
      clearTimeout(this.#hoverDelayTimer);
      this.#hoverDelayTimer = null;
    }
    this.#showFeatured();
  };

  #handleThumbsFocusOut = (event: FocusEvent): void => {
    const leavingThumbs =
      !(event.relatedTarget instanceof Node) || !this.refs.thumbs?.contains(event.relatedTarget);

    if (leavingThumbs) {
      this.#pendingImageId = null;
      this.#hoverActivated = false;
      if (this.#hoverDelayTimer) {
        clearTimeout(this.#hoverDelayTimer);
        this.#hoverDelayTimer = null;
      }
      this.#showFeatured();
    }
  };

  #showMedia(mediaId: string): void {
    if (mediaId === this.#activeImageId) return;

    const currentActive = this.#mediaCache.get(this.#activeImageId ?? "");
    currentActive?.classList.remove("product-tile__gallery-image--active");
    if (currentActive instanceof HTMLVideoElement) {
      currentActive.pause();
    }

    const media = this.#mediaCache.get(mediaId);
    media?.classList.add("product-tile__gallery-image--active");
    if (media instanceof HTMLVideoElement) {
      media.play();
    }

    for (const thumbnail of this.refs.thumbnails ?? []) {
      const isCurrent = thumbnail.dataset.mediaId === mediaId;
      thumbnail.setAttribute("aria-current", String(isCurrent));
    }

    this.#activeImageId = mediaId;
  }

  #showFeatured(): void {
    if (this.#featuredImageId) {
      this.#showMedia(this.#featuredImageId);
    }
  }
}

if (!customElements.get("tile-gallery")) {
  customElements.define("tile-gallery", TileGallery);
}
